import { watch, onMounted, type Ref } from 'vue';
import type { Message, Feedback } from '../types/chat';
import { conversationId } from './conversation';
import { filterForDisplay } from '../utils/messageFilters';

const EVENT_CATEGORY = 'chat';

/**
 * Track an event in Matomo.
 * @param category Event category.
 * @param action Event action.
 * @param name Event name (optional).
 * @param value Event value (optional, must be numeric).
 */
const trackEvent = (category: string, action: string, name?: string, value?: number) => {
    window._paq = window._paq || [];
    const eventData: any[] = ['trackEvent', category, action];

    if (name !== undefined) {
        eventData.push(name);
    }

    if (value !== undefined) {
        eventData.push(value);
    }

    window._paq.push(eventData);
};

/**
 * Track a link click in a chat message.
 * @param element The anchor element that was clicked.
 */
export const trackLinkClick = (element: HTMLAnchorElement) => {
    trackEvent(EVENT_CATEGORY, 'link_clicked', element.href);
};

/**
 * Track a streaming error.
 */
export const trackError = () => {
    trackEvent(EVENT_CATEGORY, 'error', conversationId);
};

/**
 * Track a hallucinated link error.
 * @param attemptNumber The attempt number (1 for first attempt, 2 for first retry, etc.).
 */
export const trackHallucination = (attemptNumber: number) => {
    trackEvent(EVENT_CATEGORY, 'hallucination', conversationId, attemptNumber);
};

/**
 * Track conversation deletion.
 * @param messageCount The number of messages in the conversation when it was deleted.
 */
export const trackConversationDeleted = (messageCount: number) => {
    trackEvent(EVENT_CATEGORY, 'conversation_deleted', conversationId, messageCount);
};

/**
 * Initialize chat tracking by watching the messages array.
 * @param messages Reactive reference to the messages array.
 * @param consentGiven Reactive reference to track when user gives consent.
 */
export const initializeChatTracking = (messages: Ref<Message[]>, consentGiven: Ref<boolean>) => {
    onMounted(() => {
        trackEvent(EVENT_CATEGORY, 'session_intialized', conversationId);
    });

    const stopWatchingConsent = watch(consentGiven, (isGiven) => {
        if (isGiven) {
            trackEvent(EVENT_CATEGORY, 'session_start', conversationId);
            stopWatchingConsent();
        }
    });

    const processedMessages = new WeakSet<Message>([messages.value[0]]);
    const previousFeedbackByMessage = new Map<Message, Feedback>();

    watch(
        messages,
        messages => {
            for (const message of filterForDisplay(messages)) {
                if (processedMessages.has(message)) {
                    continue;
                }

                if (message.role === 'user') {
                    trackEvent(EVENT_CATEGORY, 'user_message', conversationId, message.content.length);
                    processedMessages.add(message);
                    continue;
                }

                if (message.role === 'assistant') {
                    if (message.isStreaming) {
                        continue;
                    }

                    processedMessages.add(message);

                    const responseTime = Date.now() - message.timestamp.getTime();

                    trackEvent(EVENT_CATEGORY, 'assistant_response', conversationId, message.content.length);
                    trackEvent(EVENT_CATEGORY, 'assistant_response_time', conversationId, responseTime);

                    if (message.tool_calls && message.tool_calls.length > 0) {
                        for (const toolCall of message.tool_calls) {
                            if (toolCall.function.name !== '_meiliSearchSources') {
                                continue;
                            }

                            try {
                                const args = JSON.parse(toolCall.function.arguments);
                                const sourceCount = args.sources?.length || 0;
                                trackEvent(EVENT_CATEGORY, 'search_sources', conversationId, sourceCount);
                            } catch (error) {
                                // Ignore JSON parse errors
                            }
                        }
                    }
                }
            }
        },
        { deep: true }
    );

    watch(
        messages,
        messages => {
            for (const message of messages) {
                if (!message.feedback) {
                    continue;
                }

                const currentFeedback = message.feedback;
                const previousFeedback = previousFeedbackByMessage.get(message);

                if (currentFeedback.rating && currentFeedback.rating !== previousFeedback?.rating) {
                    const action = `feedback_${currentFeedback.rating}`;
                    trackEvent(EVENT_CATEGORY, action, conversationId);
                }

                if (currentFeedback.comment && currentFeedback.comment !== previousFeedback?.comment) {
                    trackEvent(EVENT_CATEGORY, 'feedback_comment', conversationId, currentFeedback.comment.length);
                }

                previousFeedbackByMessage.set(message, { ...currentFeedback });
            }
        },
        { deep: true }
    );
};
